import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Async thunks
export const fetchWorkAssignments = createAsyncThunk(
  'workAssignment/fetchWorkAssignments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/work-assignments');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch work assignments');
    }
  }
);

export const createWorkAssignment = createAsyncThunk(
  'workAssignment/createWorkAssignment',
  async (workAssignmentData, { rejectWithValue }) => {
    try {
      const response = await api.post('/work-assignments', workAssignmentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create work assignment');
    }
  }
);

export const updateWorkAssignment = createAsyncThunk(
  'workAssignment/updateWorkAssignment',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/work-assignments/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update work assignment');
    }
  }
);

export const deleteWorkAssignment = createAsyncThunk(
  'workAssignment/deleteWorkAssignment',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/work-assignments/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete work assignment');
    }
  }
);

const initialState = {
  workAssignments: [],
  loading: false,
  error: null,
  success: false,
  message: ''
};

const workAssignmentSlice = createSlice({
  name: 'workAssignment',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.message = '';
    },
    resetState: (state) => {
      state.workAssignments = [];
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = '';
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch work assignments
      .addCase(fetchWorkAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.workAssignments = action.payload.data || [];
      })
      .addCase(fetchWorkAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create work assignment
      .addCase(createWorkAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWorkAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
        state.workAssignments.unshift(action.payload.data);
      })
      .addCase(createWorkAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update work assignment
      .addCase(updateWorkAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWorkAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
        const index = state.workAssignments.findIndex(
          assignment => assignment.id === action.payload.data.id
        );
        if (index !== -1) {
          state.workAssignments[index] = action.payload.data;
        }
      })
      .addCase(updateWorkAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete work assignment
      .addCase(deleteWorkAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWorkAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = 'Work assignment deleted successfully';
        state.workAssignments = state.workAssignments.filter(
          assignment => assignment.id !== action.payload
        );
      })
      .addCase(deleteWorkAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSuccess, resetState } = workAssignmentSlice.actions;
export default workAssignmentSlice.reducer;
